import React from 'react';
import PropTypes from 'prop-types';
import CtaPropTypes from '../../prop-types/cta';

import * as Styled from './CTABlock.styled';

/**
 * A block that renders 1 through many CTAs
 *
 * CTAs can also render inside an overflow container
 *
 * CTAS are styled per the content system
 */
const CTABlock = ({
  isCentered,
  ctas,
  color,
  defaultVariant,
  stackOnSmall,
  withOverflow,
  ...props
}) => {
  const Wrapper = withOverflow ? Styled.Overflow : React.Fragment;

  return (
    <Styled.CTABlock
      $stackOnSmall={stackOnSmall}
      $isCentered={isCentered}
      {...props}
    >
      <Wrapper>
        {ctas.map(({ url, text, onClick, variant, color: ctaColor }, i) => (
          <Styled.CTA
            $backgroundColor={ctaColor || color}
            as={onClick && !url ? 'button' : 'a'}
            key={i}
            href={url}
            size="medium"
            variant={variant || defaultVariant}
            onClick={
              typeof onClick === 'function'
                ? (e) => {
                    e.preventDefault();
                    onClick();
                  }
                : null
            }
          >
            {text}
          </Styled.CTA>
        ))}
      </Wrapper>
    </Styled.CTABlock>
  );
};

CTABlock.propTypes = {
  /**
   * Determines if the CTAs should be centered in their container
   */
  isCentered: PropTypes.bool,
  /**
   * An array of CTAs provided from a parent
   */
  ctas: PropTypes.arrayOf(PropTypes.shape(CtaPropTypes)),
  /**
   * The color that will determine the button's background color (black or white) and text color (white or black)
   */
  color: PropTypes.string,
  /**
   * A variant type as defined in src/components/Button/Button.js
   *
   * To use this prop, do not pass a value for color.
   *
   * The color prop will otherwise override variant.
   */
  defaultVariant: PropTypes.string,
  /**
   * Indicates if the CTAs should stack on-top of each other in a small viewport.
   */
  stackOnSmall: PropTypes.bool,
  /**
   * A boolean to toggle an overflow container
   */
  withOverflow: PropTypes.bool
};

CTABlock.defaultProps = {
  isCentered: false,
  ctas: [],
  color: null,
  defaultVariant: 'defaultDark',
  stackOnSmall: false,
  withOverflow: false
};

export default CTABlock;
